import React, { useState, useEffect } from "react";
import {
  Grid,
  Typography,
  TextField,
  AppBar,
  Toolbar,
  Button,
  Paper,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import AceEditor from "react-ace";
import "ace-builds/src-noconflict/mode-markdown";
import "./aceTheme";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { solarizedlight } from "react-syntax-highlighter/dist/esm/styles/prism";
import rehypeRaw from "rehype-raw";

const theme = createTheme({
  palette: {
    primary: {
      main: "#2db49d",
    },
    secondary: {
      main: "#252527",
    },
  },
});

interface Page {
  index: number;
  name: string;
  route: string;
  category: string;
  visible: boolean;
}

interface Props {
  setSelectedIndex: React.Dispatch<React.SetStateAction<number>>;
}

export default function Admin({ setSelectedIndex }: Props) {
  const { pathname } = useLocation();
  const [category, setCategory] = useState("");
  const [name, setName] = useState("");
  const [markdown, setMarkdown] = useState("");
  const [editMode, setEditMode] = useState(true);
  const customStyle = {
    ...solarizedlight,
    'pre[class*="language-"]': {
      ...solarizedlight['pre[class*="language-"]'],
      backgroundColor: "#2f2f2f", // replace #yourColor with your desired color
    },
  };
  const renderers = {
    code: ({ node, inline, className, children, ...props }: any) => {
      const match = /language-(\w+)/.exec(className || "");
      return !inline && match ? (
        <SyntaxHighlighter
          style={customStyle}
          language={match[1]}
          PreTag="div"
          children={String(children).replace(/\n$/, "")}
          {...props}
        />
      ) : (
        <code className={className} {...props} />
      );
    },
  };
  useEffect(() => {
    setSelectedIndex(-1);
  }, [setSelectedIndex]);

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME!;
  }, [pathname]);

  const createResource = async (page: Page) => {
    try {
      const response = await fetch(
        `${
          process.env.NODE_ENV === "production"
            ? process.env.REACT_APP_API
            : "http://localhost:7000"
        }/api/resources`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(page), // Send the new page object
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(
          `HTTP error! status: ${response.status}, message: ${data.message}`
        );
      }

      return response.json();
    } catch (error) {
      console.error("There was a problem with the fetch operation: ", error);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const newPage: Page = {
      index: Date.now(),
      name: name,
      route: "/" + name,
      category: category,
      visible: true,
    };

    console.log("Page object to be sent:", newPage);

    const data = await createResource(newPage); // Send the newPage object directly, not inside an array

    console.log("Response data:", data);
  };

  const handleCancel = () => {
    // Reset the form state here
    setName("");
    setCategory("");
    setMarkdown("");
  };

  return (
    <Grid
    container
    spacing={0}
    direction="column"
    justifyContent="center" // Add this line
    alignItems="center"
    sx={{
      minHeight: '100vh', // Change this line
      position: "relative",
    }}
  >
      <AppBar position="static" style={{ width: "70%", background: "#1e1e1e" }}>
        <Toolbar style={{ width: "100%" }}>
          <Typography
            variant="h6"
            component="div"
            sx={{ flexGrow: 1, display: "flex", alignItems: "center" }}
          >
            devtools /
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              size="small"
              style={{ margin: "0 10px" }}
            />
            in
            <FormControl
              variant="outlined"
              size="small"
              style={{ margin: "0 10px", width: "140px" }}
            >
              <InputLabel id="category-label">Category</InputLabel>
              <Select
                labelId="category-label"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                label="Category"
              >
                <MenuItem value={"ai"}>Ai</MenuItem>
                <MenuItem value={"extensions"}>Extensions</MenuItem>
                <MenuItem value={"programming"}>Programming</MenuItem>
                <MenuItem value={"design"}>Design</MenuItem>
              </Select>
            </FormControl>
          </Typography>

          <ThemeProvider theme={theme}>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<CancelIcon />}
              onClick={handleCancel}
            >
              Cancel Changes
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSubmit}
              style={{ marginLeft: "10px" }}
            >
              Commit Changes
            </Button>
          </ThemeProvider>
        </Toolbar>
      </AppBar>
      <Paper
        elevation={3}
        style={{
          width: "70%",
          height: "100%",
          padding: "20px",
          background: "#252527",
        }}
      >
        <ThemeProvider theme={theme}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
    <div>
      <Button
        variant="contained"
        color={editMode ? "primary" : "secondary"}
        onClick={() => setEditMode(true)}
        style={{ marginRight: "10px" }}
      >
        Edit
      </Button>
      <Button
        variant="contained"
        color={!editMode ? "primary" : "secondary"}
        onClick={() => setEditMode(false)}
      >
        Preview
      </Button>
    </div>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => {
              setEditMode(true);
              setMarkdown(`# h1 Heading
## h2 Heading
### h3 Heading
#### h4 Heading
##### h5 Heading
###### h6 Heading

## Horizontal Rules

___

---

***

## Emphasis

**This is bold text**

__This is bold text__

*This is italic text*

_This is italic text_

## Blockquotes

> Blockquotes can also be nested...
>> ...by using additional greater-than signs right next to each other...
> > > ...or with spaces between arrows.

## Lists

Unordered

+ Create a list by starting a line with \`+\`, \`-\`, or \`*\`
+ Sub-lists are made by indenting 2 spaces:
  - Marker character change forces new list start:
    * Ac tristique libero volutpat at
    + Facilisis in pretium nisl aliquet
    - Nulla volutpat aliquam velit
+ Very easy!

Ordered

1. Lorem ipsum dolor sit amet
2. Consectetur adipiscing elit
3. Integer molestie lorem at massa

1. You can use sequential numbers...
1. ...or keep all the numbers as \`1.\`

## Code

Block code "fences"

\`\`\`
Sample text here...
\`\`\`

Syntax highlighting

\`\`\` ts
useEffect(() => {
  let title = pathname.substring(1, pathname.length);
  title = title[0].toUpperCase() + title.substring(1);
  document.title = ''${process.env.REACT_APP_NAME!}
}
\`\`\`

## Links

[link text](http://127606.vercel.app)

[link with title](http://127606.vercel.app "title text!")

## Images

![webdev](https://github.com/stiantha/stiantha/assets/132207909/bac8f9c2-37e8-4abc-ba59-fa6c384ad9ec)


`);
            }}
            style={{ marginBottom: "10px"}}
          >
            Syntax
          </Button>
          </div>
        </ThemeProvider>
        {editMode ? (
          <AceEditor
            mode="markdown"
            width="100%"
            theme="my_theme"
            value={markdown}
            onChange={setMarkdown}
            name="createResource"
            editorProps={{ $blockScrolling: true }}
            showPrintMargin={false}
            fontSize="20px"
            setOptions={{ lineHeight: "20px" }}
          />
        ) : (
          <div style={{ minHeight: '500px' }}>
          <ReactMarkdown
            components={renderers}
            children={markdown}
            rehypePlugins={[rehypeRaw]}
          />
           </div>
        )}
      </Paper>
    </Grid>
  );
}
